import { useContext, useEffect, useRef, useState } from "react";
import { usinasApi } from "../../../services/api";
import { Screen } from "../../../main/components/screen/Screen";
import EmptyStateNotFound from "../../clients/components/EmptyState";
import { Box, Grid, Typography } from "@mui/material";
import { SlEnergy } from "react-icons/sl";
import { LuGauge } from "react-icons/lu";
import MovingIcon from '@mui/icons-material/Moving';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import DashboardCard, { DashboardCardProps } from "../components/solarGridControlCards/DashboardCard";
import DiagramaUnifilar, { DiagramaUnifilarProps, DiagramSubstation, DiagramUgData, InversorData, InversorString, ReleProtecaoData } from "../components/DiagramaUnifilar";
import ControlDataStatusIndicator from "../components/solarGridControlCards/ControlDataStatusIndicator";
import { MainContext } from "../../../main/context/MainContextProvider";
import { PlantString, UG, Usina } from "../../usinas/types";
import { getNumberFromString, getOperationalStatusByIdentifier, operationalStatusToStatus } from "../utils";
import { SolarGridControlContextProvider, useSolarGridControlContext } from "../contexts/SolarGridControlContextProvider";
import { EvolutionChartContent } from "../../dashboard/components/EvolutionChart";

const USE_API_UG_DISJUNTOR_TAG = true;

function getValueAndSufix(vStr: string | null | undefined) {
    if (vStr == null) {
        return { value: "-", sufix: "" };
    }

    const regex = /([\d.,]+)\s*([^\d\s]+)/;
    const match = vStr.match(regex);
    if (!match) {
        return { value: vStr, sufix: "" };
    }

    return { value: match[1], sufix: match[2] };
}

function SolarGridControlContent() {
    const { selectedDashboardUsina } = useContext(MainContext)
    const { solarGridControlData } = useSolarGridControlContext();

    const [currentUsina, setCurrentUsina] = useState<Usina>()
    const [loading, setLoading] = useState(false);

    const elementRef = useRef(null);

    const mount = async () => {
        try {
            setLoading(true);
            setCurrentUsina(undefined);

            if (!selectedDashboardUsina) {
                return;
            }

            if (selectedDashboardUsina) {
                const usinaResponse = await usinasApi.getUsina(selectedDashboardUsina?.usinaIds.id)
                setCurrentUsina(usinaResponse)
            }
        } catch (error) {
            console.error("Erro ao carregar dados da usina do SolarGridControl", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        mount();
    }, [selectedDashboardUsina]);

    const telemetryData = solarGridControlData?.telemetryData || {
        componentOperationalStatuses: [],
    };

    const sgcdPotenciaAtivaValueAndSufix = getValueAndSufix(solarGridControlData?.potenciaAtiva);
    const sgcdTensaoDeLinhaValueAndSufix = getValueAndSufix(solarGridControlData?.tensaoDeLinha);
    const sgcdEnergiaHojeValueAndSufix = getValueAndSufix(solarGridControlData?.energiaHoje);
    const sgcdDisponibilidadeDoSistemaValueAndSufix = getValueAndSufix(solarGridControlData?.disponibilidadeDoSistema);

    const dashboardData: DashboardCardProps[] = [
        {
            backgroudIconColor: "#152c33d3",
            icon: <SlEnergy style={{ color: "green", fontSize: "20px" }} />,
            title: "Potência Ativa",
            value: sgcdPotenciaAtivaValueAndSufix.value,
            sufixx: sgcdPotenciaAtivaValueAndSufix.sufix,
            isLoading: loading || !currentUsina || solarGridControlData?.potenciaAtiva == null,
        },
        {
            backgroudIconColor: "#17263F",
            icon: <LuGauge style={{ color: "#3F61E8", fontSize: "20px" }} />,
            title: "Tensão de Linha",
            value: sgcdTensaoDeLinhaValueAndSufix.value,
            sufixx: sgcdTensaoDeLinhaValueAndSufix.sufix,
            isLoading: loading || !currentUsina || solarGridControlData?.tensaoDeLinha == null,
        },
        {
            backgroudIconColor: "#ff98001c",
            icon: <MovingIcon style={{ color: "#FF9800" }} />,
            title: "Energia Gerada",
            value: sgcdEnergiaHojeValueAndSufix.value,
            sufixx: sgcdEnergiaHojeValueAndSufix.sufix,
            isLoading: loading || !currentUsina || solarGridControlData?.energiaHoje == null,
        },
        {
            backgroudIconColor: "#212240",
            icon: <AccessTimeOutlinedIcon style={{ color: "#825DB2" }} />,
            title: "Disponibilidade (%)",
            value: sgcdDisponibilidadeDoSistemaValueAndSufix.value,
            sufixx: sgcdDisponibilidadeDoSistemaValueAndSufix.sufix,
            isLoading: loading || !currentUsina || solarGridControlData?.disponibilidadeDoSistema == null,
        },
    ];

    const getStringsData = (invIdentifier: string, strings?: PlantString) => {
        const stringsData: InversorString[] = [];
        if (!strings) {
            return stringsData
        }
        for (let i = 0; i < (strings.amount ?? 0); i++) {
            const stringIdentifier = `${invIdentifier}/STR${i + 1}`;
            const stringCurrent = solarGridControlData?.stringCurrents?.[stringIdentifier];
            stringsData.push({
                stringNumber: `String ${i + 1}`,
                corrente: stringCurrent?.value ?? /* strings.power ?? */ null,
            });
        }
        return stringsData
    }

    const getInversorData = (ug: UG) => {
        const ugNumber = getNumberFromString(ug.name);
        const ugIdentifier = `${currentUsina?.code}/UG${ugNumber}`;

        let inversoresData: InversorData[] = [];

        var firstInverterNumber = ug.components.inverters?.firstInverterNumber ?? 1;
        var maxInverterNumber = firstInverterNumber + (ug.components.inverters?.amount ?? 0) - 1;

        for (let i = firstInverterNumber; i <= maxInverterNumber; i++) {
            const invIdentifier = `${ugIdentifier}/INV${i}`;
            const invOpStatus = getOperationalStatusByIdentifier(telemetryData, invIdentifier);
            const status: InversorData["status"] = operationalStatusToStatus(invOpStatus);

            const inverterEfficiency =
                solarGridControlData?.plantaEnergiaGerada == null
                    ? "-"
                    : Number(
                        solarGridControlData.plantaEnergiaGerada.uGs
                            .find((u) => u.name === ug.name)
                            ?.inversores?.find((inv) => inv.name === `INV ${i}`)
                            ?.energiaGerada?.value ?? 0,
                    ).toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    });
            inversoresData.push({
                name: `INV ${i}`,
                efficiency: inverterEfficiency,
                status,
                strings: getStringsData(invIdentifier, ug.components.string),
            });
        }
        return inversoresData;
    }

    const getDiagramData = () => {
        // console.debug("telemetryData:", telemetryData);

        const releProtecaoData: ReleProtecaoData = {
            isActive: false,
            MWValue: 3.3,
            KVValue: 13,
            KWhValue: 4.400,
        };

        const substationIdentifier = `${currentUsina?.code}/SUB`;
        const substationOpStatus = getOperationalStatusByIdentifier(
            telemetryData,
            substationIdentifier
        );
        const DiagramSubstation: DiagramSubstation = {
            hasTransformer: currentUsina?.substation?.hasTransformer,
            hasDisjuntor: currentUsina?.substation?.hasDisjuntor,
            disjuntorTag: currentUsina?.substation?.disjuntorTag,
            hasSubstation: currentUsina?.substation?.hasSubstation,
            disjuntorStatus: operationalStatusToStatus(substationOpStatus),
            commands: currentUsina?.substation?.commands?.map((c) => c.name) ?? []
        };

        const uGs = currentUsina?.uGs ?? [];

        const ugData = uGs.map((ug) => {
            const ugNumber = getNumberFromString(ug.name);
            const ugIdentifier = `${currentUsina?.code}/UG${ugNumber}`;
            const ugSubIdentifier = `${ugIdentifier}/SUB`;
            const ugQGBTIdentifier = `${ugIdentifier}/QGBT`;

            const ugSubOpStatus = getOperationalStatusByIdentifier(telemetryData, ugSubIdentifier);
            const ugQGBTOpStatus = getOperationalStatusByIdentifier(telemetryData, ugQGBTIdentifier);

            return {
                ugName: ug.name ?? "",
                ugNumber,
                inversoresData: {
                    commands: ug.components.inverters?.commands?.map((c) => c.name),
                    inversors: getInversorData(ug),
                },
                DJBTData: {
                    status: operationalStatusToStatus(ugQGBTOpStatus),
                    disjuntorTag: USE_API_UG_DISJUNTOR_TAG ? ug.disjuntorTag ?? "DJBT" : "DJBT",
                },
                DJMTData: {
                    efficiency: 1.1,
                    status: operationalStatusToStatus(ugSubOpStatus),
                    disjuntorTag: USE_API_UG_DISJUNTOR_TAG ? ug.disjuntorTag ?? "DJMT" : "DJMT",
                },
                trafoData: { firstTemperature: 50, secondTemperature: 65, thirdTemperature: 91, hasTransformer: ug.hasTransformer ?? false },
                hasSmartLogger: ug.hasSmartLogger,
                djbtdjmtCommands: ug.djbtdjmtCommands?.map((c) => c.name) ?? []
            }
        });

        return {
            releProtecaoData: releProtecaoData,
            substation: DiagramSubstation,
            ugData: ugData as DiagramUgData[],
            showDiagramComponents: currentUsina?.fonteDadosUsina?.some(fonte => fonte.value === 'FONTE_DADOS_MQTT') ?? false,
            usinaCode: currentUsina?.code ?? "",
            usinaId: currentUsina?.usinaIds.id ?? "",
        }
    }

    const diagramData = getDiagramData();
    const isDiagramLoading = loading || !currentUsina || dashboardData.some(card => card.isLoading);
    const isChartLoading = !solarGridControlData?.evolutionChartData;

    const emptyEvolutionChartData: React.ComponentProps<
        typeof EvolutionChartContent
    >["data"] = {
        potenciaAtivaAtMinutes: [],
        cardError: {},
    };

    return (<Screen.Root>
        {selectedDashboardUsina ? (
            <>
                <Screen.Container removePadding>
                    <Grid container spacing={2} padding={2}>
                        <Grid item xs={12} md={8.25}>
                            <Box sx={{ display: "flex", alignItems: "center", marginBottom: "18px" }}>
                                <Typography
                                    sx={{
                                        fontWeight: 500,
                                        fontSize: "16px",
                                        textAlign: "left",
                                    }}
                                >
                                    Diagrama Unifilar
                                </Typography>
                                <ControlDataStatusIndicator
                                    isLoading={isDiagramLoading}
                                    loadingLabel="Carregando dados de potência ou string"
                                    readyLabel="Dados de potência e string carregados"
                                />
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", borderRadius: "8px", border: "1px solid #8F969E", justifyContent: "flex-start", width: '100%', height: '95%' }}>
                                <DiagramaUnifilar {...diagramData as DiagramaUnifilarProps} />
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={3.75}>
                            <Typography
                                sx={{
                                    fontWeight: 500,
                                    fontSize: "16px",
                                    textAlign: "left",
                                    marginBottom: "18px"
                                }}
                            >
                                Dashboard
                            </Typography>
                            {dashboardData.map((card, index) => (
                                <Box key={index} marginBottom={1}>
                                    <DashboardCard
                                        icon={card.icon}
                                        title={card.title}
                                        value={card.value}
                                        sufixx={card.sufixx}
                                        backgroudIconColor={card.backgroudIconColor}
                                        isLoading={card.isLoading}
                                    />
                                </Box>
                            ))}
                            <Box sx={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                                <Typography
                                    sx={{
                                        fontWeight: 500,
                                        fontSize: "14px",
                                        textAlign: "left",
                                    }}
                                >
                                    Potência (MW) - Últimas 24 horas
                                </Typography>
                                <ControlDataStatusIndicator
                                    isLoading={isChartLoading}
                                    loadingLabel="Carregando dados do gráfico de potência"
                                    readyLabel="Dados do gráfico de potência carregados"
                                />
                            </Box>
                            <Box
                                sx={{
                                    borderRadius: "8px",
                                    border: "1px solid #8F969E",
                                    padding: "12px",
                                }}
                            >
                                <EvolutionChartContent
                                    data={solarGridControlData?.evolutionChartData || emptyEvolutionChartData}
                                    equipmentFilter={{}}
                                    showPotenciaAtivaInCharts
                                    startWithFullRange
                                    isLoading={isChartLoading}
                                    elementRef={elementRef}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Screen.Container>
            </>
        ) : (
            <Screen.Container>
                <EmptyStateNotFound title="Nenhum dado encontrado" />
            </Screen.Container>
        )}
    </Screen.Root>
    )
}

export default function SolarGridControl() {
    return (
        <SolarGridControlContextProvider>
            <SolarGridControlContent />
        </SolarGridControlContextProvider>
    )
}
